import dayjs from "dayjs";
import Link from "next/link";
import "dayjs/locale/tr";
import BookmarkIcon from "@/components/icons/bookmark";
import LikeIcon from "@/components/icons/like";
import MessageIcon from "@/components/icons/message";
import ExtendedImage from "@/components/ui/extended-image";
import UserCard from "@/components/ui/user-card";
import PlayButton from "@/components/ui/video/play-button";
import { ShareButton } from "@/components/content/interactions";
import { cn } from "@/lib/utils";
import { ContentType } from "@/types/content";
import type { ContentCard } from "@/types/dashboard";

dayjs.locale("tr");

const SearchHorizontalContentCard = ({ content }: { content: ContentCard }) => {
  const showAuthor =
    content.author &&
    ![ContentType.Video, ContentType.Quiz].includes(content.type);

  return (
    <div className={cn("p-0 pb-8 flex flex-row gap-2 justify-between")}>
      <div
        className={cn("flex flex-col gap-2 w-full", {
          "justify-between": !showAuthor,
          "justify-end": showAuthor,
        })}
      >
        {showAuthor && <UserCard user={content.author} />}
        {content.category && (
          <span className="text-[10px] leading-3.5 font-semibold text-neutral-500">
            {content.category?.name}
          </span>
        )}
        <Link className="hover:underline" href={`/${content.slug}`}>
          <h1 className="text-[26px] leading-7.5 font-bold line-clamp-2">
            {content.title}
          </h1>
        </Link>
        <p className="text-sm leading-5 font-medium w-full line-clamp-2 mb-2">
          {content.lead || content.summary || ""}
        </p>

        <div className="flex flex-row justify-between w-full">
          <div className="mt-1 flex flex-row items-center gap-3">
            <span className="text-sm leading-5 font-semibold text-neutral-400">
              {dayjs(content.publishedAt).format("DD MMMM YYYY")}
            </span>
            <span className="inline-flex items-center justify-center gap-1 text-sm font-semibold text-neutral-400">
              <Link className="group" href={`/${content.slug}`}>
                <LikeIcon className="size-6 stroke-[1.5] stroke-neutral-400 group-hover:fill-neutral-300" />
              </Link>
              {content.likeCount}
            </span>
            <span className="inline-flex items-center justify-center gap-1 text-sm font-semibold text-neutral-400">
              <Link className="group" href={`/${content.slug}`}>
                <MessageIcon className="stroke-neutral-400 size-6 stroke-[1.5] group-hover:stroke-brand-blue-500" />
              </Link>
              {content.commentCount}
            </span>
          </div>
          <div className="flex flex-row gap-2 justify-center items-center">
            <Link className="group" href={`/${content.slug}`}>
              <BookmarkIcon className="size-6 stroke-[1.5] stroke-neutral-400 fill-none group-hover:stroke-brand-blue-500 group-hover:fill-brand-blue-100" />
            </Link>
            <ShareButton />
          </div>
        </div>
      </div>
      <div className="shrink-0 justify-center items-center flex flex-col relative w-77 h-43.5 rounded-2xl overflow-clip">
        <Link className="hover:underline" href={`/${content.slug}`}>
          <ExtendedImage
            alt={`${content.title} image`}
            className="aspect-video! w-full md:w-77"
            height={174}
            src={content.coverImageUrl || (content.thumbnailUrl as string)}
            width={308}
          />
        </Link>
        {content.type === 1 && <PlayButton slug={`/${content.slug || ""}`} />}
      </div>
    </div>
  );
};

export default SearchHorizontalContentCard;
